import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

// Configs, contexts and utils
import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { parse } from '../../util/urlHelpers';
import { isScrollingDisabled } from '../../ducks/ui.duck';

// Shared components
import { LayoutSingleColumn, NamedLink, Page } from '../../components';

// Containers
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

// Same directory
import { BLOG_ARTICLES, BLOG_CATEGORIES, getBlogCategory } from './blogArticles';
import { BlogVisual, getArticleImage } from './BlogShared';

import css from './BlogPage.module.css';

const CATEGORY_PARAM = 'metier';
const SLIDE_DURATION_MS = 5000;

// "Tous": the featured block cycles through the first article of each trade
const SLIDES = BLOG_CATEGORIES.map(c => BLOG_ARTICLES.find(a => a.category === c.id)).filter(
  Boolean
);

const POST_IMAGE_SIZES = '(max-width: 767px) 100vw, (max-width: 1023px) 50vw, 380px';

const PostCard = ({ article, image }) => (
  <NamedLink className={css.post} name="BlogArticlePage" params={{ slug: article.slug }}>
    <BlogVisual
      className={css.postVisual}
      categoryId={article.category}
      image={image}
      alt={article.title}
      sizes={POST_IMAGE_SIZES}
      showLabel
    />
    <span className={css.chip}>{getBlogCategory(article.category).label}</span>
    <h2 className={css.postTitle}>{article.title}</h2>
    <p className={css.postExcerpt}>{article.excerpt}</p>
  </NamedLink>
);

/**
 * Blog index ("Le blog des métiers"): all articles, filterable by trade category.
 *
 * @component
 * @returns {JSX.Element}
 */
const BlogIndexPage = () => {
  const intl = useIntl();
  const config = useConfiguration();
  const location = useLocation();
  const scrollingDisabled = useSelector(state => isScrollingDisabled(state));
  const categoryImages = useSelector(state => state.BlogPage.categoryImages);

  const selectedCategory = parse(location.search)[CATEGORY_PARAM] || null;
  const articles = selectedCategory
    ? BLOG_ARTICLES.filter(a => a.category === selectedCategory)
    : BLOG_ARTICLES;
  const [activeSlide, setActiveSlide] = useState(0);
  const isAllCategories = !selectedCategory;

  useEffect(() => {
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (!isAllCategories || reduceMotion || SLIDES.length < 2) {
      return undefined;
    }
    const timer = window.setInterval(
      () => setActiveSlide(i => (i + 1) % SLIDES.length),
      SLIDE_DURATION_MS
    );
    return () => window.clearInterval(timer);
  }, [isAllCategories]);

  const featured = isAllCategories ? SLIDES[activeSlide] : articles[0];
  const others = isAllCategories ? articles : articles.slice(1);

  // Position of each article among the shown articles of its category, to vary the photos
  const photoIndexBySlug = articles.reduce(
    (acc, a) => {
      const key = a.category || 'general';
      const index = acc.counts[key] || 0;
      return {
        counts: { ...acc.counts, [key]: index + 1 },
        bySlug: { ...acc.bySlug, [a.slug]: index },
      };
    },
    { counts: {}, bySlug: {} }
  ).bySlug;

  // Only show the categories that have articles
  const categories = BLOG_CATEGORIES.filter(c => BLOG_ARTICLES.some(a => a.category === c.id));

  const title = intl.formatMessage({ id: 'BlogPage.indexMetaTitle' });
  const description = intl.formatMessage({ id: 'BlogPage.indexMetaDescription' });
  const rootURL = config.marketplaceRootURL;
  const schema = {
    '@context': 'http://schema.org',
    '@type': 'Blog',
    name: intl.formatMessage({ id: 'BlogPage.indexTitle' }),
    description,
    url: `${rootURL}/blog`,
    inLanguage: 'fr-BE',
    blogPost: BLOG_ARTICLES.map(a => ({
      '@type': 'BlogPosting',
      headline: a.title,
      url: `${rootURL}/blog/${a.slug}`,
      dateModified: a.updated,
    })),
  };

  return (
    <Page
      title={title}
      description={description}
      schema={schema}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          <div className={css.container}>
            <header className={css.intro}>
              <h1 className={css.introTitle}>
                <FormattedMessage id="BlogPage.indexTitle" />
              </h1>
              <p className={css.introText}>
                <FormattedMessage id="BlogPage.indexText" />
              </p>
              <nav
                className={css.categories}
                aria-label={intl.formatMessage({ id: 'BlogPage.categoriesLabel' })}
              >
                <NamedLink
                  name="BlogPage"
                  className={selectedCategory ? css.categoryLink : css.categoryLinkActive}
                >
                  <FormattedMessage id="BlogPage.allCategories" />
                </NamedLink>
                {categories.map(c => (
                  <NamedLink
                    key={c.id}
                    name="BlogPage"
                    to={{ search: `?${CATEGORY_PARAM}=${c.id}` }}
                    className={
                      selectedCategory === c.id ? css.categoryLinkActive : css.categoryLink
                    }
                  >
                    {c.label}
                  </NamedLink>
                ))}
              </nav>
            </header>

            {featured ? (
              <div className={css.featuredWrapper}>
                <NamedLink
                  key={featured.slug}
                  className={css.featured}
                  name="BlogArticlePage"
                  params={{ slug: featured.slug }}
                >
                  <BlogVisual
                    className={css.featuredVisual}
                    categoryId={featured.category}
                    image={getArticleImage(
                      featured,
                      categoryImages,
                      photoIndexBySlug[featured.slug]
                    )}
                    alt={featured.title}
                  />
                  <div className={css.featuredOverlay}>
                    <span className={css.chip}>{getBlogCategory(featured.category).label}</span>
                    <h2 className={css.featuredTitle}>{featured.title}</h2>
                    <p className={css.featuredExcerpt}>{featured.excerpt}</p>
                  </div>
                </NamedLink>
                {isAllCategories && SLIDES.length > 1 ? (
                  <div className={css.slideDots}>
                    {SLIDES.map((a, i) => (
                      <button
                        key={a.slug}
                        type="button"
                        className={i === activeSlide ? css.slideDotActive : css.slideDot}
                        onClick={() => setActiveSlide(i)}
                        aria-label={getBlogCategory(a.category).label}
                        aria-current={i === activeSlide}
                      />
                    ))}
                  </div>
                ) : null}
              </div>
            ) : (
              <p className={css.empty}>
                <FormattedMessage id="BlogPage.noArticles" />
              </p>
            )}

            <div className={css.grid}>
              {others.map(a => (
                <PostCard
                  key={a.slug}
                  article={a}
                  image={getArticleImage(a, categoryImages, photoIndexBySlug[a.slug])}
                />
              ))}
            </div>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

export default BlogIndexPage;
